import React from 'react';
import { twMerge } from 'tailwind-merge';

export interface FilterPillProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  active?: boolean;
  size?: 'sm' | 'md' | 'lg';
  shape?: 'pill' | 'rounded';
  variant?: 'accent' | 'primary';
  children: React.ReactNode;
  /**
   * Spec 293 (Package E1): renders a built-in × dismiss affordance inside the
   * pill. The visible glyph stays inline with the pill's compact size, but the
   * click target is expanded to the 44px touch minimum via an invisible
   * pseudo-hit-area, rather than growing the pill itself. Pass a label — it
   * becomes the dismiss button's aria-label (falls back to a generic
   * "Remove filter" if omitted).
   */
  onDismiss?: () => void;
  /** Accessible label for the dismiss button. Defaults to "Remove filter". */
  dismissLabel?: string;
}

// Contract §7 pill spec: 28px height, 14px radius — both sizes converge on h-7
// (size prop kept for API compatibility; padding still differs).
const sizeClasses: Record<NonNullable<FilterPillProps['size']>, string> = {
  sm: 'h-7 px-2.5 text-xs',
  md: 'h-7 px-3 text-xs',
  // lg: 40px selector size (Spec 240 — Tasks toolbar status + scope rows as one system;
  // CEO preferred 40 over the 44 touch guideline — reads less chunky, still a fine target).
  lg: 'h-10 px-4 text-sm',
};

const shapeClasses: Record<NonNullable<FilterPillProps['shape']>, string> = {
  // rounded-full so the pill stays fully round at any height (h-7 → identical to the
  // old rounded-[14px]; h-11 needs full rounding).
  pill: 'rounded-full',
  rounded: 'rounded-md',
};

const activeClasses: Record<NonNullable<FilterPillProps['variant']>, string> = {
  accent: 'border-accent bg-accent/15 text-accent',
  primary: 'border-accent bg-accent text-text-inverse',
};

const pillLookClasses = (
  active: boolean,
  variant: NonNullable<FilterPillProps['variant']>,
  size: NonNullable<FilterPillProps['size']>,
  shape: NonNullable<FilterPillProps['shape']>,
) =>
  twMerge(
    'inline-flex items-center justify-center gap-1.5 whitespace-nowrap',
    'border font-medium transition-colors',
    sizeClasses[size],
    shapeClasses[shape],
    active
      ? activeClasses[variant]
      : 'border-border-input bg-bg-tertiary text-text-secondary hover:text-text-primary hover:border-border-medium',
  );

export const FilterPill = React.forwardRef<HTMLButtonElement, FilterPillProps>(
  (
    {
      active = false,
      size = 'md',
      shape = 'pill',
      variant = 'accent',
      className = '',
      children,
      type = 'button',
      onDismiss,
      dismissLabel = 'Remove filter',
      ...props
    },
    ref,
  ) => {
    // Dismissible mode: the pill itself is a static label (not a pressable
    // toggle), so the root must NOT be a <button> — nesting the real dismiss
    // <button> inside another <button> is invalid HTML and creates ambiguous
    // overlapping tab stops. Same shape as the pre-existing hand-rolled
    // pattern in ActiveFilterPillsRow.tsx: <span> pill + nested real <button>.
    if (onDismiss) {
      return (
        <span
          className={twMerge(pillLookClasses(active, variant, size, shape), 'cursor-default', className)}
          {...(props as React.HTMLAttributes<HTMLSpanElement>)}
        >
          {children}
          <button
            type="button"
            aria-label={dismissLabel}
            className="relative inline-flex items-center justify-center opacity-70 hover:opacity-100 bg-transparent border-none p-0 m-0 text-inherit cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              onDismiss();
            }}
          >
            {/* Invisible expanded hit area — visible glyph stays compact, tap target hits 44px */}
            <span className="absolute -inset-3" aria-hidden="true" />
            <span aria-hidden="true">&#10005;</span>
          </button>
        </span>
      );
    }

    return (
      <button
        ref={ref}
        type={type}
        aria-pressed={active}
        className={twMerge(
          pillLookClasses(active, variant, size, shape),
          'cursor-pointer',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
          'disabled:opacity-50 disabled:pointer-events-none',
          className,
        )}
        {...props}
      >
        {children}
      </button>
    );
  }
);

FilterPill.displayName = 'FilterPill';
